// Central SEO config. JSX-safe helpers on top of the pure-data route list
// in staticRoutes.mjs. To register a new page, edit staticRoutes.mjs.
//
// Everything below reads from live Supabase content where possible — the
// admin can edit titles, descriptions, business address, hours, share image,
// etc., without touching code. The static fallbacks below are used only
// when a particular field is blank in the CMS.

import { STATIC_PUBLIC_ROUTES } from "./staticRoutes.mjs";

export { STATIC_PUBLIC_ROUTES };

// Canonical origin used for absolute URLs (canonical, OG). Set REACT_APP_SITE_URL
// per environment in Cloudflare Pages.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://karenboleycounselling.com").replace(/\/$/, "");

// Static fallbacks; every field can be overridden per-install via content.seo.*
export const FALLBACK_SITE_NAME = "Karen Boley Counselling";
export const FALLBACK_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

// Return the admin-configured value or fall back to the static default.
const nonBlank = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);

export const getSiteName = (content) =>
  nonBlank(content?.seo?.siteName) || FALLBACK_SITE_NAME;

export const getTitleSuffix = (content) => {
  const raw = content?.seo?.titleSuffix;
  if (typeof raw === "string" && raw.trim()) return raw;
  return ` | ${getSiteName(content)}`;
};

export const getDefaultDescription = (content) =>
  nonBlank(content?.seo?.defaultDescription) ||
  "Warm, patient-centred counselling in Delta and Tsawwassen, BC with Karen Boley, MACP, MSEd, CCC. Support for anxiety, depression, burnout, OCD, perinatal issues, neurodivergence and couples. Book a free 20-minute consultation.";

// Resolve the OG image URL. Blank in CMS → fall back to the file bundled in /public.
export const getDefaultOgImage = (content) => {
  const configured = nonBlank(content?.seo?.defaultOgImage);
  if (!configured) return FALLBACK_OG_IMAGE;
  if (/^https?:/i.test(configured)) return configured;
  return `${SITE_URL}${configured.startsWith("/") ? "" : "/"}${configured}`;
};

// Compose a full page title: given "Home" and suffix " | Karen Boley...", returns
// "Home | Karen Boley Counselling". If the raw title already contains the suffix
// (or is complete on its own), returns as-is so admins can override cleanly.
export const composeTitle = (rawTitle, content) => {
  const suffix = getTitleSuffix(content);
  const t = (rawTitle || "").trim();
  if (!t) return getSiteName(content);
  if (t.includes(getSiteName(content))) return t;
  return `${t}${suffix}`;
};

// Look up per-page SEO overrides (title + description) from CMS. Returns
// { title, description } — either field may be null when the admin left it
// blank, in which case the page component substitutes its own default.
export const getPageSeo = (content, pageKey) => {
  const p = content?.seo?.pages?.[pageKey] || {};
  return {
    title: nonBlank(p.title),
    description: nonBlank(p.description),
  };
};

// -- Structured data: LocalBusiness --------------------------------------
// Rebuilds every render so any admin edit to Contact/Booking/About/Treatment
// or the SEO section flows into Google's rich results.
export const buildLocalBusinessJsonLd = (content) => {
  const office = content?.contact?.office || {};
  const booking = content?.booking || {};
  const about = content?.about || {};
  const treatment = content?.treatment || {};

  // Address: use the structured admin fields. Country is hardcoded to CA
  // (Karen's practice is Canadian) — no need to expose that in the admin.
  const street = nonBlank(office.streetAddress);
  const city = nonBlank(office.city);
  const region = nonBlank(office.region);
  const postal = nonBlank(office.postalCode);
  const address = (street || city || region || postal)
    ? {
        "@type": "PostalAddress",
        ...(street && { streetAddress: street }),
        ...(city && { addressLocality: city }),
        ...(region && { addressRegion: region }),
        ...(postal && { postalCode: postal }),
        addressCountry: "CA",
      }
    : undefined;

  // Opening hours: read from `booking.hours` structured entries. Days can be
  // a comma-separated string or already an array; opens/closes are 24h strings.
  const dayMap = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
  const normDay = (raw) => {
    if (!raw) return null;
    const key = String(raw).trim().toLowerCase().slice(0, 3);
    return dayMap[key] || null;
  };
  const openingHoursSpecification = (Array.isArray(booking.hours) ? booking.hours : [])
    .map((h) => {
      const rawDays = Array.isArray(h.days) ? h.days : String(h.days || "").split(",");
      const dayOfWeek = rawDays.map(normDay).filter(Boolean);
      const opens = nonBlank(h.opens);
      const closes = nonBlank(h.closes);
      if (!dayOfWeek.length || !opens || !closes) return null;
      return {
        "@type": "OpeningHoursSpecification",
        dayOfWeek,
        opens,
        closes,
        ...(nonBlank(h.label) && { description: h.label }),
      };
    })
    .filter(Boolean);

  // Services / topics — pull tile names from admin so new treatment areas
  // flow into the "knowsAbout" search-engine hint automatically.
  const knowsAbout = (Array.isArray(treatment.areas) ? treatment.areas : [])
    .map((a) => nonBlank(a?.name))
    .filter(Boolean);

  // Credentials
  const hasCredential = (Array.isArray(about.credentials) ? about.credentials : [])
    .map((c) => nonBlank(c))
    .filter(Boolean);

  return {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "ProfessionalService"],
    "@id": `${SITE_URL}#business`,
    name: getSiteName(content),
    description: nonBlank(content?.seo?.businessDescription) || getDefaultDescription(content),
    url: SITE_URL,
    image: getDefaultOgImage(content),
    ...(nonBlank(booking.rate) && { priceRange: `${booking.rate} CAD` }),
    ...(nonBlank(office.phone) && { telephone: office.phone }),
    ...(address && { address }),
    areaServed: [
      { "@type": "City", name: nonBlank(office.city) || "Delta" },
      { "@type": "City", name: "Tsawwassen" },
      { "@type": "AdministrativeArea", name: "British Columbia" },
    ],
    ...(openingHoursSpecification.length && { openingHoursSpecification }),
    founder: {
      "@type": "Person",
      "@id": `${SITE_URL}#karen`,
      name: nonBlank(about.name) || "Karen Boley",
      jobTitle: "Canadian Certified Counsellor",
      ...(hasCredential.length && { hasCredential }),
    },
    ...(knowsAbout.length && { knowsAbout }),
  };
};
